import { useCallback, useEffect, useState } from 'react'
import { Layout, LayoutContent } from '@astryxdesign/core/Layout'
import { Stack } from '@astryxdesign/core/Stack'
import { Avatar } from '@astryxdesign/core/Avatar'
import { Badge } from '@astryxdesign/core/Badge'
import { Button } from '@astryxdesign/core/Button'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { TextInput } from '@astryxdesign/core/TextInput'
import { List, ListItem } from '@astryxdesign/core/List'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { Spinner } from '@astryxdesign/core/Spinner'
import StoreBadges from '../../components/StoreBadges'
import { listCoaches } from './coachApi'
import { APP_STORE_URL, PLAY_STORE_URL } from './bookingLink'
import { clubsLabel, coachName, openCountLabel } from './coachText'

// padelr.app/coach - every coach on Padelr, searchable by name
// (hidden-beacon-3). Each row opens the coach's own page, which is where their
// créneaux are.

// Typing is not a request per keystroke.
const SEARCH_DEBOUNCE_MS = 300

export function CoachListView() {
    const [search, setSearch] = useState('')
    const [query, setQuery] = useState('')
    const [coaches, setCoaches] = useState(null)
    const [nextToken, setNextToken] = useState('')
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
        let live = true
        setCoaches(null)
        setFailed(false)
        listCoaches(query, '')
            .then(result => {
                if (!live) return
                setCoaches((result && result.list) || [])
                setNextToken((result && result.next_token) || '')
            })
            .catch(() => { if (live) setFailed(true) })
        return () => { live = false }
    }, [query])

    const loadMore = useCallback(() => {
        setLoadingMore(true)
        listCoaches(query, nextToken)
            .then(result => {
                setCoaches(current => [...(current || []), ...((result && result.list) || [])])
                setNextToken((result && result.next_token) || '')
            })
            .catch(() => setFailed(true))
            .finally(() => setLoadingMore(false))
    }, [query, nextToken])

    useEffect(() => { document.title = 'Trouver un coach · Padelr' }, [])

    let body
    if (failed) {
        body = <EmptyState title="Liste indisponible" description="Nous n'avons pas pu charger les coachs. Réessayez dans un instant." />
    } else if (coaches === null) {
        body = (
            <Stack gap={4} hAlign="center" paddingBlock={10}>
                <Spinner />
            </Stack>
        )
    } else if (coaches.length === 0) {
        body = (
            <EmptyState
                title="Aucun coach trouvé"
                description={query ? `Aucun coach ne correspond à « ${query} ».` : "Aucun coach n'est encore inscrit."}
            />
        )
    } else {
        body = (
            <Stack gap={4}>
                <List hasDividers>
                    {coaches.map(coach => {
                        const open = openCountLabel(coach.num_open)
                        return (
                            <ListItem
                                key={coach.username}
                                href={`/coach/${encodeURIComponent(coach.username)}`}
                                startContent={<Avatar size="md" name={coachName(coach)} />}
                                label={coachName(coach)}
                                description={clubsLabel(coach.clubs) || `@${coach.username}`}
                                endContent={open && <Badge variant="info" label={open} />}
                            />
                        )
                    })}
                </List>
                {nextToken && (
                    <Stack hAlign="center">
                        <Button label="Plus de coachs" onClick={loadMore} isLoading={loadingMore} />
                    </Stack>
                )}
            </Stack>
        )
    }

    return (
        <Layout padding={6} contentWidth={640} height="auto">
            <LayoutContent padding={6}>
                <Stack gap={8}>
                    <Stack gap={2}>
                        <Heading level={1}>Trouver un coach</Heading>
                        <Text as="p" type="large" color="secondary">
                            Les coachs de padel sur Padelr et leurs cours ouverts aux inscriptions.
                        </Text>
                    </Stack>
                    <TextInput
                        label="Rechercher un coach"
                        isLabelHidden
                        placeholder="Nom du coach"
                        value={search}
                        onChange={value => setSearch(value)}
                    />
                    {body}
                    <Stack gap={3} hAlign="center">
                        <Text type="supporting">Réservez vos cours dans l'application</Text>
                        <StoreBadges appStoreURL={APP_STORE_URL} playStoreURL={PLAY_STORE_URL} />
                    </Stack>
                </Stack>
            </LayoutContent>
        </Layout>
    )
}

export default CoachListView
